import { RenderAskUserQuestionTool } from './AskUserQuestionTool.render.tsx';
import { type AskUserQuestionParameters, AskUserQuestionParametersSchema, type StepperPayload } from './schema.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';

/**
 * Unified tool for asking user questions via interactive slides.
 */

export const AskUserQuestionTool = createTool('askUserQuestion', () => ({
  instructions: (
    <ToolGuidance
      tool={AskUserQuestionTool}
      whenNotToUse={[
        'User just answered a question — do work first, then ask again if needed',
        'Simple factual question — answer directly without follow-up',
        'Only one entity is found — proceed directly without asking',
        'Only one option available — the tool requires minimum 2 options per slide, skip the question',
      ]}
      whenToUse={[
        'Multiple matches from query — let user select',
        'Ambiguous request — clarify intent with options derived from data',
        'After presenting data — offer drill-downs, comparisons, or related views',
        'Complex tasks (diagnosis, root cause analysis) — gather scope and parameters through guided multi-slide questions before executing',
      ]}
    >
      <h3>Follow-up Questions</h3>
      <dl>
        <dt>After a dashboard</dt>
        <dd>
          Offer 2-4 context-specific actions: compare with another entity, drill into a metric, check related data
          (alarms, status), change time range
        </dd>
        <dt>After a diagnosis</dt>
        <dd>Offer investigation paths: check specific views, compare against baselines, review historical trends</dd>
        <dt>Multi-step planning</dt>
        <dd>For complex requests, use multiple slides to gather scope, time range, and metrics before querying</dd>
      </dl>

      <h3>Option Descriptions</h3>
      <ul>
        <li>Include a description for each option unless the label is self-explanatory (e.g., "Yes", "No").</li>
        <li>For entities from queries, include relevant metadata: "Producer, Oil" or "Injector, Water, Offline".</li>
        <li>Provide minimum 2 options per slide; options must be context-specific based on the data just returned.</li>
      </ul>

      <h3>Slide Types</h3>
      <dl>
        <dt>option</dt>
        <dd>Single choice (disambiguation)</dd>
        <dt>checkbox</dt>
        <dd>Multiple selections</dd>
        <dt>button</dt>
        <dd>Quick actions</dd>
        <dt>confirmation</dt>
        <dd>Yes/No</dd>
      </dl>
    </ToolGuidance>
  ),
  config: {
    parameters: AskUserQuestionParametersSchema,
    render: RenderAskUserQuestionTool,
    description: <p>Ask the user a question via interactive slides.</p>,
    execute: async (
      { slides }: AskUserQuestionParameters,
      { human }: { human: (payload: unknown) => Promise<unknown> },
    ) => {
      // Collect responses for each slide
      const responses: Record<string, string[]> = {};

      for (const [i, slide] of slides.entries()) {
        // Create stepper payload for this slide
        const payload: StepperPayload = {
          type: 'stepper',
          slideIndex: i,
          totalSlides: slides.length,
          slide,
        };

        const response = (await human(payload)) as { selectedIds: string[] };
        responses[slide.title] = slide.options
          .filter((opt) => response.selectedIds.includes(opt.id))
          .map((opt) => opt.label);
      }

      return {
        success: true,
        data: responses,
        messages: [
          {
            role: 'assistant',
            content: `I have the following questions for you:

${slides.map((s, i) => `${i + 1}. ${s.question}`).join('\n')}`,
          },
          {
            role: 'user',
            content: `Here are my answers to your questions:

${
  Object.entries(responses)
    .map(([title, choices], i) => `${i + 1}. ${title}: ${choices.join(', ')}`)
    .join('\n')
    .trim() || ''
}`,
          },
          {
            role: 'system',
            content: `Based on the user's responses, determine the appropriate next steps. If the user provided no responses, proceed with a default action or ask a clarifying question.`,
          },
        ],
      };
    },
  },
}));
